import { api } from "@convex/_generated/api";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useAction, useQuery as useConvexQuery } from "convex/react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { AddPaymentMethodStep } from "~/components/dashboard/add-payment-step";
import { CompleteDashboardStep } from "~/components/dashboard/complete-dashboard";
import { ConfirmSubscriptionStep } from "~/components/dashboard/confirm-subscription-step";
import { LoadingStep } from "~/components/dashboard/loading-step";
import { SetupPosStep } from "~/components/dashboard/setup-pos-step";
import { StripeConnectStep } from "~/components/dashboard/stripe-connect-step";
import { useActiveOrganization } from "~/integrations/vortex-auth/hooks";
import { logger } from "~/lib/logger";

import { PageWrapper } from "../components/page-wrapper";
import { OverviewSkeleton } from "../components/skeletons/overview-skeleton";

// Avoids TS2589 (type instantiation is excessively deep) from api.d.ts chain
const checkOnboardingStatusRef = api.stripeAccounts.checkOnboardingStatus;

const completeOnboardingAndCreateSubscriptionRef =
  api.stripeAccounts.completeOnboardingAndCreateSubscription;

const createSetupIntentRef = api.stripeSetupIntents.createSetupIntent;

const getDefaultPaymentMethodRef =
  api.stripeSetupIntents.getDefaultPaymentMethod;

const getCurrentBillingInfoRef = api.billing.getCurrentBillingInfo;

const getPosReadinessStatusRef = api.billing.getPosReadinessStatus;

export const Route = createFileRoute("/_authenticated/")({
  component: Index,
  pendingComponent: OverviewSkeleton,
});

type OnboardingStep =
  | "stripe-connect"
  | "add-payment-method"
  | "confirm-subscription"
  | "setup-pos"
  | "complete";

const handlePaymentMethodError = (error: Error) => {
  toast.error(error.message || "Failed to save payment method");
};

function Index() {
  const { organization } = useActiveOrganization();
  const checkOnboardingStatus = useAction(checkOnboardingStatusRef);
  const completeOnboardingAndCreateSubscription = useAction(
    completeOnboardingAndCreateSubscriptionRef
  );
  const createSetupIntent = useAction(createSetupIntentRef);
  const getDefaultPaymentMethod = useAction(getDefaultPaymentMethodRef);

  const orgData = useConvexQuery(getCurrentBillingInfoRef);
  const posReadiness = useConvexQuery(getPosReadinessStatusRef);

  const [onboardingStep, setOnboardingStep] = useState<OnboardingStep | null>(
    null
  );
  const [isCreatingSubscription, setIsCreatingSubscription] = useState(false);
  const [setupIntentClientSecret, setSetupIntentClientSecret] = useState<
    string | null
  >(null);
  const [isLoadingSetupIntent, setIsLoadingSetupIntent] = useState(false);

  const {
    data: onboardingStatus,
    isLoading: isCheckingOnboarding,
    refetch: refetchOnboarding,
  } = useQuery({
    queryKey: ["stripe", "onboardingStatus", organization?.id],
    queryFn: () =>
      checkOnboardingStatus({ organizationId: organization?.id ?? "" }),
    enabled: !!organization,
    refetchOnWindowFocus: true,
  });

  const {
    data: paymentMethodData,
    isLoading: isCheckingPaymentMethod,
    refetch: refetchPaymentMethod,
  } = useQuery({
    queryKey: ["stripe", "paymentMethod", organization?.id],
    queryFn: () =>
      getDefaultPaymentMethod({ organizationId: organization?.id ?? "" }),
    enabled: !!organization && onboardingStatus?.completed,
  });

  const stripeConnectComplete = onboardingStatus?.completed ?? false;
  const hasPaymentMethod = paymentMethodData?.hasPaymentMethod ?? false;
  const hasActiveSubscription =
    orgData?.tier === "free" || orgData?.subscriptionStatus === "active";
  const isPosReady = posReadiness?.isReady ?? false;
  const isFullyOnboarded =
    stripeConnectComplete && hasActiveSubscription && isPosReady;

  useEffect(() => {
    if (
      isCheckingOnboarding ||
      orgData === undefined ||
      posReadiness === undefined
    ) {
      return;
    }

    if (stripeConnectComplete && isCheckingPaymentMethod) {
      return;
    }

    if (isFullyOnboarded) {
      setOnboardingStep("complete");
    } else if (
      stripeConnectComplete &&
      !hasPaymentMethod &&
      !hasActiveSubscription
    ) {
      setOnboardingStep("add-payment-method");
    } else if (
      stripeConnectComplete &&
      hasPaymentMethod &&
      !hasActiveSubscription
    ) {
      setOnboardingStep("confirm-subscription");
    } else if (hasActiveSubscription && !isPosReady) {
      setOnboardingStep("setup-pos");
    } else {
      setOnboardingStep("stripe-connect");
    }
  }, [
    isCheckingOnboarding,
    orgData,
    isFullyOnboarded,
    stripeConnectComplete,
    hasPaymentMethod,
    hasActiveSubscription,
    isCheckingPaymentMethod,
    isPosReady,
    posReadiness,
  ]);

  useEffect(() => {
    const loadSetupIntent = async () => {
      if (
        onboardingStep === "add-payment-method" &&
        !setupIntentClientSecret &&
        organization &&
        !isLoadingSetupIntent
      ) {
        setIsLoadingSetupIntent(true);
        try {
          const result = await createSetupIntent({
            organizationId: organization.id,
          });
          setSetupIntentClientSecret(result.clientSecret);
        } catch (error) {
          logger.exception("setup_intent_creation_failed", error);
          toast.error("Failed to load payment form. Please try again.");
        } finally {
          setIsLoadingSetupIntent(false);
        }
      }
    };

    void loadSetupIntent();
  }, [
    onboardingStep,
    setupIntentClientSecret,
    organization,
    createSetupIntent,
    isLoadingSetupIntent,
  ]);

  if (!organization) {
    return null;
  }

  const handleStripeConnectExit = async () => {
    const result = await refetchOnboarding();
    if (result.data?.completed) {
      setOnboardingStep("add-payment-method");
    }
  };

  const handlePaymentMethodSuccess = async () => {
    toast.success("Payment method saved!");
    await refetchPaymentMethod();
    setSetupIntentClientSecret(null);
    setOnboardingStep("confirm-subscription");
  };

  const handleCreateSubscription = async () => {
    setIsCreatingSubscription(true);
    try {
      const result = await completeOnboardingAndCreateSubscription({
        organizationId: organization.id,
      });

      if (result.success) {
        toast.success("Subscription created successfully!");
        setOnboardingStep("complete");
        window.location.reload();
      } else {
        toast.error(result.error ?? "Failed to create subscription");
      }
    } catch (error) {
      logger.exception("subscription_creation_failed", error);
      toast.error("Failed to create subscription. Please try again.");
    } finally {
      setIsCreatingSubscription(false);
    }
  };

  const isAbandonedOnboarding = stripeConnectComplete && !hasActiveSubscription;

  const renderOnboardingContent = () => {
    if (isCheckingOnboarding || onboardingStep === null) {
      return <LoadingStep />;
    }

    if (onboardingStep === "complete" || isFullyOnboarded) {
      return <CompleteDashboardStep />;
    }

    if (onboardingStep === "stripe-connect") {
      return (
        <StripeConnectStep
          onExit={handleStripeConnectExit}
          organizationId={organization.id}
        />
      );
    }

    if (onboardingStep === "add-payment-method") {
      return (
        <AddPaymentMethodStep
          clientSecret={setupIntentClientSecret}
          isLoading={isLoadingSetupIntent}
          onError={handlePaymentMethodError}
          onSuccess={handlePaymentMethodSuccess}
        />
      );
    }

    if (onboardingStep === "confirm-subscription") {
      return (
        <ConfirmSubscriptionStep
          isAbandonedOnboarding={isAbandonedOnboarding}
          isCreating={isCreatingSubscription}
          onSubscribe={handleCreateSubscription}
        />
      );
    }

    if (onboardingStep === "setup-pos") {
      return <SetupPosStep posReadiness={posReadiness} />;
    }

    return null;
  };

  return (
    <PageWrapper
      description="Your financial dashboard and account balance"
      title="Overview"
    >
      <div className="flex flex-col gap-6">{renderOnboardingContent()}</div>
    </PageWrapper>
  );
}
